import '@fontsource/cinzel/600.css';
import '@fontsource/cinzel/800.css';
// Tylko alfabet łaciński z polskimi znakami (latin-ext) — bez cyrylicy i greki.
import '@fontsource/alegreya-sans/latin-400.css';
import '@fontsource/alegreya-sans/latin-ext-400.css';
import '@fontsource/alegreya-sans/latin-500.css';
import '@fontsource/alegreya-sans/latin-ext-500.css';
import '@fontsource/alegreya-sans/latin-700.css';
import '@fontsource/alegreya-sans/latin-ext-700.css';
import '@fontsource/alegreya/latin-400.css';
import '@fontsource/alegreya/latin-ext-400.css';
import '@fontsource/alegreya/latin-400-italic.css';
import '@fontsource/alegreya/latin-ext-400-italic.css';
import './style.css';
import { content, type Slot } from '@gra/content';
import {
  chooseTalent,
  createGame,
  createProfile,
  equip,
  equipmentModifiers,
  equippedItems,
  equipmentPower,
  pull,
  claimLoot,
  rewardRun,
  setClass,
  setRace,
  step,
  unequip,
  upgradeItem,
  upgradeModifiers,
  buyUpgrade,
  activeFrame,
  buyFeatured,
  claimAchievement,
  setFrame,
  setTitle,
  updateAchievements,
  upgradeItemWithShards,
  type GameState,
  type Profile,
} from '@gra/engine';
import { sfx } from './audio';
import { GameInput, KeyboardInput, TouchJoystick } from './input';
import { music } from './music';
import { Transition } from './ui/transition';
import { loadProfile, saveProfile } from './profile-store';
import { Assets } from './three/assets';
import { HubScene } from './three/hub-scene';
import { installPaintedShading } from './three/painted';
import { IconRenderer } from './three/icons';
import { RunScene, type HeroLook } from './three/run-scene';
import { Stage } from './three/stage';
import { allModelPaths, armorModel, BODY_MODEL, hubModelPaths, RACE_MODELS } from './three/visuals';
import type { BodyLook } from './three/body';
import { el } from './ui/dom';
import { HubUi } from './ui/hub-ui';
import { RunUi } from './ui/run-ui';
import { achievementToast } from './ui/achievement-toast';

// Malowany styl dla wszystkich modeli — musi być przed pierwszą kompilacją shaderów.
installPaintedShading();

/** Stały krok symulacji — render chodzi z dowolnym FPS. */
const DT = 1 / 60;

const randomSeed = () => (Math.random() * 2 ** 31) | 0;

/** Gołe ciało rasy i płci: skóra, włosy, proporcje (races.json → look). */
function bodyLook(profile: Profile): BodyLook {
  const look = content.races[profile.raceId]!.look;
  const female = profile.gender === 'k';
  return {
    skinTone: look.skinTone,
    hair: (female && look.female.hair) || look.hair,
    build: (female && look.female.build) || look.build,
    female,
  };
}

/** Wygląd bohatera z profilu: model klasy, przedmioty z rzadkością i poświata wg siły ekwipunku. */
function heroLook(profile: Profile): HeroLook {
  const items = equippedItems(content, profile);
  const tiers = items.map(({ inst }) => content.rarityOrder.indexOf(inst.rarity));
  const best = Math.max(0, ...tiers);
  const maxTier = content.rarityOrder.length - 1;
  return {
    // Gołe ciało: model rasy i płci z AI (race-models.json), a bez niego manekin w barwach rasy.
    visual: RACE_MODELS[`${profile.raceId}-${profile.gender}`] ? `race:${profile.raceId}-${profile.gender}` : BODY_MODEL,
    body: { ...bodyLook(profile), textured: !!RACE_MODELS[`${profile.raceId}-${profile.gender}`] },
    armor: items
      .filter(({ def }) => def.look)
      .map(({ def, inst }) => ({
        slot: def.slot,
        ...def.look!,
        rarity: content.rarities[inst.rarity]!.color,
        tier: content.rarityOrder.indexOf(inst.rarity),
        modelPath: armorModel(def.id, profile.gender),
      })),
    twoHanded: items.some(({ def }) => def.twoHanded),
    // Przedmiot z modelem części stroju (np. kaptur) zakłada body.ts — bez osobnego modelu przedmiotu.
    equipment: items.filter(({ def }) => !armorModel(def.id, profile.gender)).map(({ def, inst }) => ({
      visual: def.visual,
      color: content.rarities[inst.rarity]!.color,
      tier: content.rarityOrder.indexOf(inst.rarity),
    })),
    aura: {
      color: best > 0 ? content.rarities[content.rarityOrder[best]!]!.color : null,
      strength: tiers.reduce((a, b) => a + b, 0) / (4 * maxTier),
    },
  };
}

async function main(): Promise<void> {
  const loading = el('div', 'screen loading');
  const bar = el('div', 'bar xp');
  const fill = el('div', 'fill');
  bar.append(fill);
  loading.append(el('h1', '', 'Echa Pustkowi'), bar);
  document.getElementById('ui')!.append(loading);

  const stage = new Stage(document.getElementById('game')!);
  const uiRoot = document.getElementById('ui')!;
  uiRoot.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('button')) sfx.click();
  });
  uiRoot.addEventListener('pointerover', (e) => {
    const card = (e.target as HTMLElement).closest('.card, .item-card, .slot');
    if (card && !card.contains(e.relatedTarget as Node)) sfx.hover();
  });
  const assets = new Assets();
  const tLoad = performance.now();
  // Najpierw tylko obóz; wrogowie i dekoracje stref dociągają się w tle, zanim gracz wyruszy.
  const hubPaths = hubModelPaths();
  await assets.load(hubPaths, (done, total) => (fill.style.width = `${(done / total) * 100}%`));
  const tSmooth = performance.now();
  // Zaokrąglanie brył i miękkie cieniowanie: upieczone modele są gotowe, resztę obrabiamy teraz.
  const unbaked = assets.processUnbaked();
  if (import.meta.env.DEV && unbaked) console.info(`[wczytywanie] ${unbaked} modeli bez obróbki z góry — uruchom npm run asset:bake`);
  if (import.meta.env.DEV) console.info(`[wczytywanie] modele ${Math.round(tSmooth - tLoad)} ms, wygładzanie ${Math.round(performance.now() - tSmooth)} ms`);
  loading.remove();
  const hubSet = new Set(hubPaths);
  const runAssets = assets.load(allModelPaths().filter((p) => !hubSet.has(p))).then(() => assets.processUnbaked());

  let profile = loadProfile(content) ?? createProfile(content, 'straznik', randomSeed());
  // Uzupełnia puste sloty przedmiotami startowymi (np. po migracji starego zapisu).
  setClass(content, profile, profile.classId);
  const persist = () => saveProfile(profile);
  persist();

  const icons = new IconRenderer(assets);
  const hub = new HubScene(assets, stage.renderer.domElement);
  const input = new GameInput(new KeyboardInput(window), new TouchJoystick(document.getElementById('game')!));
  const runUi = new RunUi(content);

  let game: GameState | null = null;
  let run: RunScene | null = null;
  let reward = 0;
  let paused = false;
  let accumulator = 0;
  /** Osiągnięcia w trakcie wyprawy sprawdzamy co sekundę, nie co klatkę. */
  let achievementTimer = 0;

  // Nowo zdobyte osiągnięcia: powiadomienie i zapis. W trakcie wyprawy liczy się też jej bieżący stan.
  const checkAchievements = (run?: GameState) => {
    const fresh = updateAchievements(content, profile, run);
    // Kilka naraz (np. po pierwszej wyprawie): dwa powiadomienia i podsumowanie reszty.
    const shown = fresh.length > 3 ? fresh.slice(0, 2) : fresh;
    for (const def of shown) achievementToast(def.name);
    if (fresh.length > shown.length) achievementToast(`i ${fresh.length - shown.length} kolejnych`, 'Osiągnięcia zdobyte');
    if (fresh.length > 0) {
      sfx.reveal(2);
      persist();
    }
  };

  const refreshHub = () => {
    checkAchievements();
    persist();
    hub.setHero(heroLook(profile));
    hubUi.render(profile);
  };

  const hubUi = new HubUi(content, icons, {
    equip: (uid) => {
      equip(content, profile, uid);
      sfx.equip();
      refreshHub();
    },
    unequip: (slot: Slot) => {
      unequip(profile, slot);
      refreshHub();
    },
    setRace: (raceId, gender) => {
      setRace(content, profile, raceId, gender);
      sfx.equip();
      refreshHub();
    },
    setClass: (classId) => {
      setClass(content, profile, classId);
      refreshHub();
    },
    pull: (count, bannerId) => {
      const results = pull(content, profile, count, { banner: bannerId, now: Date.now() });
      refreshHub();
      hubUi.showResults(results);
      if (results.some((r) => content.rarityOrder.indexOf(r.item.rarity) >= content.rarityOrder.length - 2)) hub.cheer();
    },
    buyUpgrade: (id) => {
      buyUpgrade(content, profile, id);
      sfx.levelUp();
      refreshHub();
    },
    upgrade: (uid) => {
      upgradeItem(content, profile, uid);
      sfx.reveal(1);
      refreshHub();
    },
    upgradeShards: (uid) => {
      upgradeItemWithShards(content, profile, uid);
      sfx.reveal(1);
      refreshHub();
    },
    buyFeatured: (bannerId, itemId) => {
      const result = buyFeatured(content, profile, bannerId, itemId, Date.now());
      refreshHub();
      hubUi.showResults([result]);
    },
    claimAchievement: (id) => {
      claimAchievement(content, profile, id);
      sfx.levelUp();
      refreshHub();
    },
    setTitle: (id) => {
      setTitle(content, profile, id);
      sfx.click();
      refreshHub();
    },
    setFrame: (id) => {
      setFrame(content, profile, id);
      sfx.click();
      refreshHub();
    },
    startRun: (zoneId) => void startRun(zoneId),
  });

  const transition = new Transition();
  const toHub = async (titled = true) => {
    await transition.cover(titled ? 'Obóz' : '', titled ? 'Powrót' : '');
    run?.dispose();
    run = null;
    game = null;
    runUi.hideAll();
    stage.show(hub);
    hub.setHero(heroLook(profile));
    hubUi.show(profile);
    transition.reveal(250);
  };

  let lastZone = Object.keys(content.zones)[0]!;
  let starting = false;
  const startRun = async (zoneId = lastZone) => {
    if (starting) return;
    starting = true;
    lastZone = zoneId;
    await transition.cover(content.zones[zoneId]?.name ?? '', 'Wyprawa');
    await runAssets;
    // Wyprawa dostaje ekwipunek i stałe ulepszenia z drzewka rozwoju.
    const loadout = [...equipmentModifiers(content, profile), ...upgradeModifiers(content, profile)];
    const nextGame = createGame(content, profile.classId, randomSeed(), loadout, zoneId, profile.raceId, equipmentPower(content, profile));
    const nextRun = new RunScene(assets, nextGame, heroLook(profile));
    // Shadery kompilujemy, zanim pokażemy scenę — bez czarnego ekranu i bez zacięcia przy pierwszym wrogu.
    nextRun.prewarm();
    try {
      await stage.renderer.compileAsync(nextRun.scene, nextRun.camera);
    } catch {
      // starsze przeglądarki: skompiluje się przy pierwszej klatce
    }
    nextRun.releasePrewarm();
    run?.dispose();
    game = nextGame;
    run = nextRun;
    reward = 0;
    paused = false;
    accumulator = 0;
    hubUi.hide();
    runUi.show(nextGame);
    runUi.setFrame(activeFrame(content, profile)?.color ?? null);
    achievementTimer = 0;
    stage.show(nextRun);
    transition.reveal(450);
    starting = false;
  };

  const setPaused = (value: boolean) => {
    if (!game || game.over || game.pendingChoices) return;
    paused = value;
    runUi.setPaused(paused);
  };
  runUi.onTogglePause = () => setPaused(!paused);
  window.addEventListener('keydown', (e) => {
    if (e.code === 'Escape' || e.code === 'KeyP') setPaused(!paused);
  });
  // Przełączenie aplikacji na tablecie albo karty w przeglądarce: pauza, żeby nie zginąć w tle.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) setPaused(true);
  });

  let last = performance.now();
  const frame = (now: number) => {
    const frameDt = Math.min((now - last) / 1000, 0.25);
    last = now;
    const g = game;

    const scales = run ? run.timeScales(frameDt) : { sim: 1, visual: 1 };
    if (g && run && !paused && !g.over && !g.pendingChoices) {
      accumulator += frameDt * scales.sim;
      while (accumulator >= DT) {
        run.snapshot();
        step(g, input.moveDirection(), DT);
        run.handleEvents(g.events);
        for (const e of g.events) {
          if (e.type === 'enemyHit') sfx.hit(e.crit);
          else if (e.type === 'playerHurt') {
            runUi.flashHurt();
            sfx.hurt();
          } else if (e.type === 'levelUp') sfx.levelUp();
          else if (e.type === 'gameOver') sfx.gameOver();
          else if (e.type === 'chestOpened') sfx.chest();
          else if (e.type === 'explosion') sfx.explosion();
          else if (e.type === 'bossSpawned') {
            sfx.bossRoar();
            const def = content.enemies[e.defId];
            const kind = e.final ? 'Boss końcowy' : 'Boss';
            runUi.announce(def?.name ?? 'Boss', def?.title ? `${kind} · ${def.title}` : `${kind} nadciąga`);
          } else if (e.type === 'bossEnraged') {
            sfx.bossRoar();
            runUi.announce('Szał!', `${content.enemies[e.defId]?.name ?? 'Boss'} wpada w furię`);
          } else if (e.type === 'runEventStart') {
            sfx.reveal(1);
            runUi.announce(e.name, e.description);
          } else if (e.type === 'bossDefeated') {
            sfx.reveal(3);
            if (!e.final) runUi.announce('Boss pokonany', 'Skrzynia czeka');
          } else if (e.type === 'victory') runUi.announce('Zwycięstwo!', content.zones[g.zoneId]?.name ?? '');
          else if (e.type === 'enemyWindup') sfx.windup();
          else if (e.type === 'pickup') {
            if (e.kind === 'gold') sfx.coin();
            else if (e.kind === 'potion') sfx.potion();
            else if (e.kind === 'relic' && e.relicId && e.rarity) {
              sfx.reveal(content.rarityOrder.indexOf(e.rarity));
              runUi.relicToast(e.relicId);
            } else if (e.itemId && e.rarity) {
              sfx.reveal(content.rarityOrder.indexOf(e.rarity));
              runUi.lootToast(e.itemId, e.rarity);
            }
          }
        }
        accumulator -= DT;
        if (g.over || g.pendingChoices) {
          accumulator = 0;
          break;
        }
      }
    }

    if (g && !g.over && !paused) {
      achievementTimer += frameDt;
      if (achievementTimer >= 1) {
        achievementTimer = 0;
        checkAchievements(g);
      }
    }

    if (g?.pendingChoices && !runUi.levelUpOpen) {
      runUi.showLevelUp(g, (id) => {
        chooseTalent(g, id);
        runUi.hideLevelUp();
      });
    }
    if (g?.over && !runUi.gameOverOpen) {
      const previousBest = profile.stats.bestTime;
      reward = rewardRun(content, profile, g);
      const loot = claimLoot(content, profile, g);
      // Wyprawa jest już w statystykach profilu — sprawdzamy bez niej, żeby nie liczyć jej dwa razy.
      checkAchievements();
      persist();
      runUi.showGameOver(g, reward, loot, previousBest, () => void startRun(), () => void toHub());
    }

    // Pozycje rysujemy między ostatnimi dwoma krokami symulacji, więc ruch jest płynny przy każdym odświeżaniu ekranu.
    run?.setAlpha(accumulator / DT);
    // Po śmierci scena dalej się animuje (upadek bohatera), symulacja stoi.
    stage.frame(paused || g?.pendingChoices ? 0 : frameDt * scales.visual);
    if (g) runUi.update(g);
    if (run && g && !g.over) runUi.updateMarkers(run.offscreenMarkers(window.innerWidth, window.innerHeight));
    else runUi.updateMarkers([]);
    // Joystick tylko w trakcie wyprawy; muzyka wg sytuacji (obóz, wyprawa, walka z bossem).
    input.touch.enabled = !!g && !g.over && !g.pendingChoices && !paused;
    const enraged = !!g && g.enemies.some((e) => e.enraged && e.hp > 0);
    music.setMood(!g ? 'hub' : enraged ? 'enrage' : g.bossIds.length > 0 ? 'boss' : 'run');
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);

  void toHub(false);

  // Narzędzia w konsoli przeglądarki podczas developmentu:
  //   __game()     — stan wyprawy        __skip(30) — przewiń wyprawę o 30 s
  //   __profile()  — profil gracza       __gems(5000) — dodaj kryształy
  //   __reset()    — nowy profil
  //   __hub()      — scena obozu (np. podgląd animacji bohatera)
  //   __give('kostna-klinga', 'legendarny') — dodaj i załóż przedmiot (test nowych modeli, zmienia klasę w razie potrzeby)
  //   __walk(1, 0, 2) — idź w prawo przez 2 s, klatka po klatce (podgląd ruchu, gdy karta nie dostaje requestAnimationFrame)
  // ?debug w adresie: te same narzędzia na serwerze testowym (diagnoza błędów obrazu na karcie gracza).
  if (import.meta.env.DEV || new URLSearchParams(location.search).has('debug')) {
    Object.assign(window, {
      __give: (itemId: string, rarity = content.rarityOrder[0]!) => {
        const def = content.items[itemId];
        if (!def || !content.rarities[rarity]) throw new Error(`Nie ma ${itemId} / ${rarity}`);
        // Przedmiot innej klasy: przełącz bohatera, żeby dało się go obejrzeć.
        if (!def.classes.includes(profile.classId)) setClass(content, profile, def.classes[0]!);
        const uid = profile.nextUid++;
        profile.inventory.push({ uid, itemId, rarity, level: 1 });
        equip(content, profile, uid);
        refreshHub();
      },
      __game: () => game,
      __run: () => run,
      __hub: () => hub,
      __stage: () => stage,
      __profile: () => profile,
      __gems: (n: number) => {
        profile.currency += n;
        refreshHub();
      },
      __reset: () => {
        profile = createProfile(content, 'straznik', randomSeed());
        refreshHub();
      },
      __walk: (moveX: number, moveY: number, seconds: number) => {
        const g = game;
        if (!g || !run) return;
        for (let t = 0; t < seconds && !g.over && !g.pendingChoices; t += DT) {
          run.snapshot();
          step(g, { moveX, moveY }, DT);
          run.handleEvents(g.events);
          run.setAlpha(1);
          stage.frame(DT);
        }
      },
      __skip: (seconds: number) => {
        const g = game;
        if (!g || !run) return;
        for (let t = 0; t < seconds && !g.over; t += DT) {
          if (g.pendingChoices) chooseTalent(g, g.pendingChoices[0]!);
          step(g, { moveX: 0, moveY: 0 }, DT);
          run.handleEvents(g.events);
        }
        runUi.hideLevelUp();
      },
    });
  }
}

void main();
